import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import QRScanModal from './QRScanModal';

const SUGGEST_MIN_CHARS = 2;
const SUGGEST_DEBOUNCE_MS = 250;

export default function QRLookupModal({ open, onClose, onResolved }) {
  const { t } = useTranslation(['nav', 'common']);
  const [code, setCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const query = code.trim();
    if (!open || query.length < SUGGEST_MIN_CHARS) {
      setSuggestions([]);
      return;
    }
    const thisRequestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      api.get('/lookup/suggest', { params: { q: query } })
        .then(res => {
          if (requestIdRef.current === thisRequestId) setSuggestions(res.data?.results || []);
        })
        .catch(() => {
          if (requestIdRef.current === thisRequestId) setSuggestions([]);
        });
    }, SUGGEST_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [code, open]);

  if (!open) return null;

  const runLookup = (value) => {
    if (!value) {
      setError(t('lookup.empty'));
      return;
    }
    setShowSuggestions(false);
    setLoading(true);
    setError('');
    api.get('/lookup', { params: { code: value } })
      .then(res => onResolved(res.data))
      .catch(err => {
        if (err.response?.status === 404) setError(t('lookup.notFound'));
        else setError(t('lookup.failed'));
      })
      .finally(() => setLoading(false));
  };

  const handleScanned = (scannedCode) => {
    setScanning(false);
    setCode(scannedCode);
    runLookup(scannedCode);
  };

  const handleClose = () => {
    setCode('');
    setError('');
    setSuggestions([]);
    onClose();
  };

  const handleSelectSuggestion = (suggestion) => {
    setCode(suggestion.code);
    setSuggestions([]);
    runLookup(suggestion.code);
  };

  return (
    <div style={overlayStyle} onClick={handleClose}>
      <div role="dialog" aria-modal="true" aria-label={t('lookup.title')} style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{t('lookup.title')}</h3>
        <div style={{ position: 'relative' }}>
          <input
            value={code}
            onChange={e => { setCode(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            placeholder={t('lookup.placeholder')}
            title={t('tip.lookupInput')}
            dir="ltr"
            autoComplete="off"
            style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul style={suggestListStyle}>
              {suggestions.map(s => (
                <li key={`${s.type}-${s.code}`}
                  onMouseDown={() => handleSelectSuggestion(s)}
                  title={t('tip.lookupSuggestion')}
                  style={suggestItemStyle}
                  dir="ltr"
                >
                  <span>{s.code}</span>
                  <span style={{ fontSize: 11, color: 'var(--c-text-muted-fg)', marginInlineStart: 8 }}>{t(`lookup.types.${s.type}`)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {error && <p style={{ color: 'var(--c-danger-fg)', fontSize: 13, marginTop: 8 }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 12 }}>
          <button type="button" onClick={() => setScanning(true)} title={t('tip.lookupScan')} style={btnStyle('var(--c-info)')}>{t('lookup.scanBtn')}</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={handleClose} title={t('common:tip.cancel')} style={btnStyle('var(--c-text-muted)')}>{t('common:cancel')}</button>
            <button type="button" disabled={loading} onClick={() => runLookup(code)} title={t('tip.lookupSubmit')} style={btnStyle('var(--c-success)')}>
              {t('lookup.submit')}
            </button>
          </div>
        </div>

        <QRScanModal open={scanning} onDetected={handleScanned} onClose={() => setScanning(false)} />
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
};

const suggestListStyle = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 2001,
  background: 'var(--c-surface)', border: '1px solid var(--c-border-strong)', borderRadius: 6,
  margin: '2px 0 0', padding: 0, listStyle: 'none',
  maxHeight: 200, overflowY: 'auto',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
};

const suggestItemStyle = {
  padding: '8px 10px', cursor: 'pointer', fontSize: 13,
  borderBottom: '1px solid var(--c-bg-muted)',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};

const modalStyle = {
  background: 'var(--c-surface)', borderRadius: 10, padding: 24, width: 360, maxWidth: '90vw',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};

const btnStyle = (bg) => ({
  background: bg, color: 'var(--c-on-accent)', border: 'none', borderRadius: 6,
  padding: '6px 14px', cursor: 'pointer', fontSize: 13,
});
